/**
 * Message video player for the PM thread. pm/partials/message-body.twig renders the
 * bubble, and a tap reads the playback links from GET /v1/video/{id}.
 */

import { formatMinutesSeconds } from '../../modules/minutes-seconds.js';

// Backend VideoPolicy::PLAYBACK_URL_TTL_SECONDS sets the life of a link.
const VIDEO_LINK_TTL_MS = 600000;
// A link is used only while it can still play the clip to the end.
const VIDEO_LINK_EXPIRY_MARGIN_MS = 30000;
const VIDEO_MIN_LINK_LIFETIME_MS = 60000;
// A link that fails before it expires spends one recovery, so a broken clip cannot loop.
const VIDEO_MAX_LINK_RECOVERIES = 2;
const VIDEO_POLL_INTERVAL_MS = 2000;
const VIDEO_POLL_BUDGET_MS = 120000;
// The link limit counts per viewer, so one 429 holds back the reads of every clip.
const VIDEO_RATE_LIMIT_BACKOFF_MS = 60000;
const VIDEO_UNAVAILABLE_RECHECK_MS = 300000;

// HTMLMediaElement error codes and readyState values.
const VIDEO_MEDIA_ERR_NETWORK = 2;
const VIDEO_MEDIA_ERR_DECODE = 3;
const VIDEO_MEDIA_ERR_SRC_NOT_SUPPORTED = 4;
const VIDEO_HAVE_METADATA = 1;
const VIDEO_HAVE_CURRENT_DATA = 2;

const VIDEO_PANELS = ['idle', 'loading', 'processing', 'failed', 'unavailable', 'error'];

// Mirrors MessageVideoPresenter::shape and the box classes of pm/partials/message-body.twig.
const VIDEO_BOXES = {
    landscape: { minRatio: 1.5, width: 'w-[22.5rem]', aspect: 'aspect-video' },
    wide: { minRatio: 1.15, width: 'w-[22.5rem]', aspect: 'aspect-[4/3]' },
    square: { minRatio: 0.87, width: 'w-[22.5rem]', aspect: 'aspect-square' },
    tall: { minRatio: 0.65, width: 'w-[16.875rem]', aspect: 'aspect-[3/4]' },
    portrait: { minRatio: 0, width: 'w-[12.625rem]', aspect: 'aspect-[9/16]' },
};

/** Links per clip id, shared by every bubble of the clip on the page. */
const videoLinks = new Map();

/** The last clip element that claimed playback. A new claim pauses it and every voice message. */
let playingVideoElement = null;

/** The target of the latest press or focus move, so a late settle does not take focus back. */
let lastInteractionTarget = null;
['pointerdown', 'focusin', 'click'].forEach((type) => {
    document.addEventListener(type, (event) => {
        lastInteractionTarget = event.target;
    }, true);
});

/** Every element with a source, so that one whose bubble left the page stops its download. */
const startedVideoElements = new Set();

let videoRateLimitedUntil = 0;

/** Counts the taps and starts of a clip or voice message, so only the latest request autoplays. */
let videoPlaybackClaims = 0;

/**
 * The time until which a link can still start and finish the clip. The response carries no
 * server time, so `urls_expire_at` is read on the device clock and capped at the link TTL.
 */
function videoLinkUsableUntil(urlsExpireAt, durationMs, now) {
    const stated = typeof urlsExpireAt === 'string' ? Date.parse(urlsExpireAt) : NaN;
    if (Number.isNaN(stated)) return NaN;
    const expiresAt = Math.min(stated, now + VIDEO_LINK_TTL_MS);
    const clipMs = typeof durationMs === 'number' && durationMs > 0 ? durationMs : 0;

    return Math.max(expiresAt - (clipMs + VIDEO_LINK_EXPIRY_MARGIN_MS), now + VIDEO_MIN_LINK_LIFETIME_MS);
}

/** The box shape nearest to the clip ratio, or null for a clip of unknown size. */
function videoShape(width, height) {
    if (!(width > 0) || !(height > 0)) return null;
    const ratio = width / height;

    return Object.keys(VIDEO_BOXES).find((shape) => ratio >= VIDEO_BOXES[shape].minRatio);
}

/** The clip length as M:SS, like the `duration` macro of components/video.twig. */
function formatVideoLength(durationMs) {
    if (typeof durationMs !== 'number' || !(durationMs > 0)) return '';

    return formatMinutesSeconds(Math.max(1, Math.round(durationMs / 1000)));
}

/** A network error, or a source error once the link expired, is fixed by new links. A decode error is not. */
function isRenewableVideoError(code, isLinkExpired) {
    return code === VIDEO_MEDIA_ERR_NETWORK
        || (isLinkExpired && (code === undefined || code === VIDEO_MEDIA_ERR_SRC_NOT_SUPPORTED));
}

/**
 * Reads a clip and returns what the bubble shows. The kinds are ready (with the links),
 * processing, failed, unavailable, rateLimited and error.
 */
async function readVideoClip(videoId, now, own) {
    // An owner read spends no link, so the backoff skips it.
    if (!own && now() < videoRateLimitedUntil) return { kind: 'rateLimited' };

    let response;
    try {
        response = await window.ApiClient.request('/video/' + encodeURIComponent(videoId), { method: 'GET' });
    } catch (e) {
        return { kind: 'error' };
    }
    if (response.status === 404) return { kind: 'unavailable' };
    if (response.status === 429) {
        videoRateLimitedUntil = now() + VIDEO_RATE_LIMIT_BACKOFF_MS;
        return { kind: 'rateLimited' };
    }
    if (!response.ok) return { kind: 'error' };

    let clip;
    try {
        clip = await response.json();
    } catch (e) {
        return { kind: 'error' };
    }

    switch (clip && clip.status) {
        case 'ready': {
            const usableUntil = videoLinkUsableUntil(clip.urls_expire_at, clip.duration_ms, now());
            if (!clip.video_url || Number.isNaN(usableUntil)) return { kind: 'error' };
            const link = {
                kind: 'ready',
                videoUrl: String(clip.video_url),
                posterUrl: clip.poster_url ? String(clip.poster_url) : '',
                usableUntil,
                width: Number(clip.width),
                height: Number(clip.height),
                durationMs: typeof clip.duration_ms === 'number' ? clip.duration_ms : null,
            };
            videoLinks.set(videoId, link);
            return link;
        }
        case 'processing':
            return { kind: 'processing' };
        case 'failed':
            return { kind: 'failed' };
        default:
            // A payload answer is final, so it offers no recheck.
            return { kind: 'unavailable', final: true };
    }
}

function claimVideoPlayback(element) {
    videoPlaybackClaims += 1;
    if (playingVideoElement && playingVideoElement !== element) {
        playingVideoElement.pause();
    }
    playingVideoElement = element;
    document.querySelectorAll('.audio-player audio').forEach((audio) => {
        if (!audio.paused) audio.pause();
    });
}

function releaseVideoElement(element) {
    startedVideoElements.delete(element);
    if (playingVideoElement === element) playingVideoElement = null;
    element.pause();
    // A detached element keeps its download running until its source is cleared.
    element.removeAttribute('src');
    element.load();
}

function releaseDetachedVideos() {
    startedVideoElements.forEach((element) => {
        if (!element.isConnected) releaseVideoElement(element);
    });
}

// audio-player.js pauses a playing clip through this function.
window.pauseVideoPlayback = function () {
    videoPlaybackClaims += 1;
    if (playingVideoElement) playingVideoElement.pause();
};

/**
 * Wires one bubble. `now` and `schedule` are injectable for tests.
 */
function initVideoPlayer(container, now = Date.now, schedule = setTimeout) {
    const videoId = container.dataset.videoId;
    const frame = container.querySelector('[data-video-frame]');
    const poster = container.querySelector('[data-video-poster]');
    const duration = container.querySelector('[data-video-duration]');
    const checkAgain = container.querySelector('[data-video-check-again]');
    const recheck = container.querySelector('[data-video-recheck]');
    const retryButton = container.querySelector('[data-video-retry]');
    if (!videoId || !frame) return null;

    const idleButton = container.querySelector('[data-video-panel="idle"]');
    const own = container.dataset.videoOwn === '1';
    let video = null;
    let videoLink = null;
    let videoAutoplay = false;
    let reading = false;
    let resumeAt = 0;
    let recoveries = 0;
    let pollStartedAt = 0;
    let pollTickAt = 0;
    let pollDelay = 0;
    // The playback count when the viewer asked, or when this clip last started.
    let askedAtClaims = 0;
    let startedAtClaims = 0;
    let pollPending = false;
    // Set when the viewer pressed a control in this bubble, so the next settle moves focus.
    let wantsFocus = false;

    function show(panel, errorKind) {
        VIDEO_PANELS.forEach((name) => {
            const element = container.querySelector('[data-video-panel="' + name + '"]');
            if (element) element.hidden = name !== panel;
        });
        container.querySelectorAll('[data-video-error]').forEach((element) => {
            element.hidden = element.dataset.videoError !== (errorKind || 'load');
        });
        if (duration) {
            duration.hidden = panel === 'failed' || panel === 'unavailable' || panel === null || !duration.textContent;
        }
        if (panel !== 'processing' && checkAgain) checkAgain.hidden = true;
        if (panel !== 'unavailable' && recheck) recheck.hidden = true;
        if (panel !== 'idle' && poster) poster.hidden = true;
        container.dataset.videoState = panel === null ? 'playing' : panel;
    }

    /** A clip read while still processing on the server has its size and length only once ready. */
    function applyClip(link) {
        const shape = videoShape(link.width, link.height);
        if (shape && shape !== container.dataset.videoShape) {
            const before = VIDEO_BOXES[container.dataset.videoShape] || VIDEO_BOXES.tall;
            container.classList.replace(before.width, VIDEO_BOXES[shape].width);
            frame.classList.replace(before.aspect, VIDEO_BOXES[shape].aspect);
            container.dataset.videoShape = shape;
        }
        const length = formatVideoLength(link.durationMs);
        if (length && duration && !duration.textContent) {
            duration.textContent = length;
            if (idleButton && container.dataset.videoPlayLabel) {
                idleButton.setAttribute('aria-label', container.dataset.videoPlayLabel.replace('%duration%', length));
            }
        }
    }

    /** Focus moves only while the viewer is still in this bubble when the read settles. */
    function focusOnce(element) {
        const active = document.activeElement;
        const free = (!active || active === document.body || container.contains(active))
            && (!lastInteractionTarget || container.contains(lastInteractionTarget));
        if (wantsFocus && free && element && typeof element.focus === 'function') element.focus({ preventScroll: true });
        wantsFocus = false;
    }

    function removeVideo() {
        if (!video) return;
        // The viewer's last press or focus was on this element, so it stays in the bubble.
        if (lastInteractionTarget === video) lastInteractionTarget = frame;
        releaseVideoElement(video);
        video.remove();
        video = null;
    }

    /** The poster shows only after a play, from a link that is still valid. */
    function showIdle(link) {
        removeVideo();
        show('idle');
        if (poster && link && link.posterUrl && link.usableUntil > now()) {
            poster.src = link.posterUrl;
            poster.hidden = false;
        }
    }

    function startVideo(link, autoplay) {
        removeVideo();
        show(null);
        videoLink = link;
        const element = document.createElement('video');
        element.className = 'absolute inset-0 h-full w-full bg-gray-900 object-contain';
        element.controls = true;
        element.playsInline = true;
        element.preload = autoplay ? 'auto' : 'none';
        element.disablePictureInPicture = true;
        element.setAttribute('aria-label', container.dataset.videoLabel || '');
        if (link.posterUrl) element.poster = link.posterUrl;
        const startAt = resumeAt;
        element.addEventListener('loadedmetadata', () => {
            if (startAt > 0) element.currentTime = startAt;
        });
        // A clip that plays to its end fires pause before ended. A scrub to the end fires no pause.
        let playedToEnd = false;
        element.addEventListener('play', () => {
            if (element !== video) return;
            playedToEnd = false;
            claimVideoPlayback(element);
            startedAtClaims = videoPlaybackClaims;
        });
        element.addEventListener('seeking', () => {
            playedToEnd = false;
        });
        element.addEventListener('pause', () => {
            if (element !== video) return;
            // A paused clip is no longer a request to play, so a renewal keeps it paused.
            videoAutoplay = false;
            playedToEnd = element.ended;
        });
        // The ended clip goes back to its play button, so the phone gestures reach the bubble again.
        // A clip in fullscreen stays, so the viewer is not thrown out.
        element.addEventListener('ended', () => {
            if (element !== video || !playedToEnd || document.fullscreenElement === element
                || element.webkitDisplayingFullscreen) return;
            resumeAt = 0;
            wantsFocus = document.activeElement === element;
            showIdle(link);
            focusOnce(idleButton);
        });
        element.addEventListener('error', () => onMediaError(element));
        element.src = link.videoUrl;
        startedVideoElements.add(element);
        frame.appendChild(element);
        video = element;
        videoAutoplay = autoplay;
        focusOnce(element);
        if (autoplay) {
            const started = element.play();
            // A browser that refuses the start leaves the controls for the viewer to press.
            if (started && typeof started.catch === 'function') started.catch(() => {});
        }
    }

    function showUnavailable(final) {
        removeVideo();
        show('unavailable');
        focusOnce(frame);
        if (!final && recheck) {
            schedule(() => {
                if (container.isConnected && container.dataset.videoState === 'unavailable') recheck.hidden = false;
            }, VIDEO_UNAVAILABLE_RECHECK_MS);
        }
    }

    function showError(kind) {
        removeVideo();
        show('error', kind);
        focusOnce(retryButton);
    }

    /** Applies a read. `mode` is play (the viewer asked), renew (a player error) or poll. */
    function settle(result, mode, autoplay) {
        switch (result.kind) {
            case 'ready':
                applyClip(result);
                if (mode === 'poll') {
                    show('idle');
                    focusOnce(idleButton);
                } else {
                    startVideo(result, autoplay && videoPlaybackClaims === askedAtClaims);
                }
                return;
            case 'processing':
                removeVideo();
                show('processing');
                focusOnce(frame);
                startPoll();
                return;
            case 'failed':
                removeVideo();
                show('failed');
                focusOnce(frame);
                return;
            case 'unavailable':
                showUnavailable(result.final);
                return;
            case 'rateLimited':
                showError('rate');
                return;
            default:
                if (mode === 'poll') {
                    show('processing');
                    if (checkAgain) checkAgain.hidden = false;
                    return;
                }
                showError('load');
        }
    }

    async function read(mode, autoplay) {
        if (reading) return;
        reading = true;
        if (mode !== 'poll') show('loading');
        let result;
        try {
            result = await readVideoClip(videoId, now, own);
        } finally {
            reading = false;
        }
        if (!container.isConnected) return;
        settle(result, mode, autoplay);
    }

    function play(fromStart) {
        if (reading) return;
        wantsFocus = true;
        videoPlaybackClaims += 1;
        askedAtClaims = videoPlaybackClaims;
        if (fromStart) resumeAt = 0;
        recoveries = 0;
        const cached = videoLinks.get(videoId);
        if (cached && cached.usableUntil > now()) {
            applyClip(cached);
            startVideo(cached, true);
            return;
        }
        read('play', true);
    }

    function onMediaError(element) {
        if (element !== video || !container.isConnected) return;
        const code = element.error ? element.error.code : undefined;
        const isLinkExpired = !videoLink || videoLink.usableUntil <= now();
        // An element without metadata reports 0, so the last known position stays.
        if (element.readyState >= VIDEO_HAVE_METADATA) resumeAt = element.currentTime;
        // A first play the viewer asked for that has no frame yet counts as playing.
        const autoplay = !element.paused
            || (videoAutoplay && element.readyState < VIDEO_HAVE_CURRENT_DATA && element.played.length === 0);
        wantsFocus = document.activeElement === element;
        // A playing clip renews with autoplay only while no newer request came after its start.
        if (!element.paused) askedAtClaims = startedAtClaims;

        // Only a decode error keeps the link for Retry. A source error can be a 403 or 404 from storage.
        if (code !== VIDEO_MEDIA_ERR_DECODE && videoLinks.get(videoId) === videoLink) videoLinks.delete(videoId);
        if (!isRenewableVideoError(code, isLinkExpired)
            || (!isLinkExpired && recoveries >= VIDEO_MAX_LINK_RECOVERIES)) {
            showError('load');
            return;
        }
        if (!isLinkExpired) recoveries += 1;
        removeVideo();
        read('renew', autoplay);
    }

    function schedulePoll(delay) {
        pollPending = true;
        pollTickAt = now();
        pollDelay = delay;
        schedule(pollTick, delay);
    }

    function pollTick() {
        pollPending = false;
        if (!container.isConnected || container.dataset.videoState !== 'processing') return;
        // Hidden time, rate-limit backoff and the lateness of a throttled timer do not count against the budget.
        const elapsed = now() - pollTickAt;
        pollStartedAt += document.hidden ? elapsed : Math.max(0, elapsed - Math.min(pollDelay, VIDEO_POLL_INTERVAL_MS));
        if (document.hidden) {
            schedulePoll(VIDEO_POLL_INTERVAL_MS);
            return;
        }
        pollPending = true;
        readVideoClip(videoId, now, own).then((result) => {
            pollPending = false;
            if (!container.isConnected || container.dataset.videoState !== 'processing') return;
            if (result.kind === 'processing' || result.kind === 'rateLimited') {
                if (now() - pollStartedAt < VIDEO_POLL_BUDGET_MS) {
                    schedulePoll(own ? VIDEO_POLL_INTERVAL_MS : Math.max(VIDEO_POLL_INTERVAL_MS, videoRateLimitedUntil - now()));
                } else if (checkAgain) {
                    checkAgain.hidden = false;
                }
                return;
            }
            settle(result, 'poll', false);
        });
    }

    function startPoll(delay = VIDEO_POLL_INTERVAL_MS) {
        if (pollPending) return;
        pollStartedAt = now();
        schedulePoll(delay);
    }

    if (idleButton) idleButton.addEventListener('click', () => play(true));
    if (retryButton) retryButton.addEventListener('click', () => play(false));
    if (recheck) recheck.addEventListener('click', () => play(true));
    if (checkAgain) {
        checkAgain.addEventListener('click', () => {
            wantsFocus = true;
            if (typeof frame.focus === 'function') frame.focus({ preventScroll: true });
            checkAgain.hidden = true;
            startPoll(0);
        });
    }

    const status = container.dataset.videoStatus;
    container.dataset.videoState = status === 'ready' ? 'idle' : status;
    if (status === 'processing') startPoll();

    return { play, onMediaError, get video() { return video; } };
}

function initAllVideoPlayers(root) {
    (root || document).querySelectorAll('.pm-video:not([data-initialized])').forEach((element) => {
        element.setAttribute('data-initialized', '1');
        initVideoPlayer(element);
    });
}

window.initVideoPlayers = initAllVideoPlayers;

function autoInitVideoPlayers() {
    initAllVideoPlayers();

    // Thread switches, paging, the poll and the socket insert server-rendered bubbles here.
    const messagesContainer = document.getElementById('threads_right');
    if (messagesContainer) {
        const observer = new MutationObserver(() => {
            releaseDetachedVideos();
            if (document.querySelector('.pm-video:not([data-initialized])')) {
                initAllVideoPlayers();
            }
        });
        observer.observe(messagesContainer, { childList: true, subtree: true });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInitVideoPlayers);
} else {
    autoInitVideoPlayers();
}
